import type { AuthTestimonialCopy } from "./auth-split-layout";

export interface AuthResetPasswordCopy {
  title: string;
  description: string;
  newPasswordPlaceholder: string;
  confirmPasswordPlaceholder: string;
  passwordsDontMatch: string;
  updateButton: string;
}

export interface AuthInvitationCopy {
  loading: string;
  errorTitle: string;
  notFound: string;
  noId: string;
  loadFailed: string;
  acceptFailed: string;
  goToDashboard: string;
  alreadyAcceptedTitle: string;
  alreadyAcceptedDescription: string;
  expiredTitle: string;
  revokedTitle: string;
  invalidDescription: string;
  emailMismatchTitle: string;
  emailMismatchDescription: string;
  emailMismatchHelp: string;
  signInDifferentAccount: string;
  title: string;
  description: string;
  organizationLabel: string;
  roleLabel: string;
  invitedEmailLabel: string;
  expiresLabel: string;
  signedInAs: string;
  accepting: string;
  acceptButton: string;
  signInToAcceptHelp: string;
  signInToAccept: string;
  acceptAsHelp: string;
  /** Checkbox label for customer orgs; {{terms}} and {{privacy}} become links. */
  acceptTermsCustomer: string;
  /** Checkbox label for agency orgs; {{agreement}} becomes a link. */
  acceptTermsPartner: string;
  termsLinkText: string;
  privacyLinkText: string;
  partnerAgreementLinkText: string;
  termsUrl: string;
  privacyUrl: string;
  partnerAgreementUrl: string;
}

export interface AuthErrorCopy {
  unexpected: string;
  invalidCredentials: string;
  emailExists: string;
  passwordTooShort: string;
  invalidEmail: string;
  signupDisabled: string;
  rateLimited: string;
  linkExpired: string;
  somethingWentWrong: string;
  acceptTerms: string;
  passwordRequirementsPrefix: string;
  inviteExpired: string;
  invitationExpired: string;
  accessDenied: string;
  contactAdminResend: string;
  resetAllRequirements: string;
  resetPasswordsDontMatch: string;
  resetFailed: string;
  resetGeneric: string;
  verifyHuman: string;
  passwordLeaked: string;
  passwordSame: string;
}

// All localized copy for the auth surfaces (sign-in / sign-up / forgot-password
// / reset-password). Shape mirrors `auth` in the shared dictionaries; pages pass
// `dictionary.auth` straight in, and the compiler verifies it matches.
export interface AuthCopy {
  testimonial: AuthTestimonialCopy;
  signInTitle: string;
  signInDescription: string;
  signUpTitle: string;
  signUpDescription: string;
  forgotTitle: string;
  forgotDescription: string;
  google: string;
  microsoft: string;
  orContinueWith: string;
  emailLabel: string;
  emailPlaceholder: string;
  passwordLabel: string;
  forgotPasswordLink: string;
  signInButton: string;
  createAccountButton: string;
  sendResetLinkButton: string;
  loginButton: string;
  createNewAccountButton: string;
  dontHaveAccount: string;
  alreadyHaveAccount: string;
  signUpLink: string;
  signInLink: string;
  termsAgree: string;
  termsOfService: string;
  termsAnd: string;
  privacyPolicy: string;
  passwordStrength: string;
  reqMinChars: string;
  reqUppercase: string;
  reqLowercase: string;
  reqNumber: string;
  verificationSent: string;
  resetLinkSent: string;
  trySigningIn: string;
  requestNewInvitation: string;
  orSeparator: string;
  resetPassword: AuthResetPasswordCopy;
  errors: AuthErrorCopy;
  invitation: AuthInvitationCopy;
}

// English fallback used when a consumer hasn't threaded the localized dictionary
// through yet. Kept in sync with dictionaries/en.json#auth.
export const DEFAULT_AUTH_COPY: AuthCopy = {
  testimonial: {
    quote: "When I have an ongoing consultation I'm not able to answer the phone. There's peace of mind in knowing that the phone is still answered.",
    author: "Michela",
    role: "Owner, Visage Studio",
    integrationConnect: "connected through",
    integrationDetail: "through Verbu, and today Verbu books appointments whenever Michela isn't available and when customers write online.",
    logosHeading: "Other companies using Verbu include",
  },
  signInTitle: "Welcome back",
  signInDescription: "Enter your email and password to access your account",
  signUpTitle: "Create your account",
  signUpDescription: "Enter your details to create your account",
  forgotTitle: "Reset your password",
  forgotDescription: "Enter your email and we'll send you a reset link",
  google: "Google",
  microsoft: "Microsoft",
  orContinueWith: "Or continue with",
  emailLabel: "Email",
  emailPlaceholder: "name@example.com",
  passwordLabel: "Password",
  forgotPasswordLink: "Forgot password?",
  signInButton: "Sign In",
  createAccountButton: "Create Account",
  sendResetLinkButton: "Send reset link",
  loginButton: "Login",
  createNewAccountButton: "Create a new account",
  dontHaveAccount: "Don't have an account?",
  alreadyHaveAccount: "Already have an account?",
  signUpLink: "Sign up",
  signInLink: "Sign in",
  termsAgree: "I agree to the",
  termsOfService: "Terms of Service",
  termsAnd: "and",
  privacyPolicy: "Privacy Policy",
  passwordStrength: "Password Strength",
  reqMinChars: "At least 8 characters",
  reqUppercase: "Contains uppercase letter",
  reqLowercase: "Contains lowercase letter",
  reqNumber: "Contains number",
  verificationSent: "Check your email for a verification link!",
  resetLinkSent: "If an account exists with this email, we've sent a password reset link. Check your inbox.",
  trySigningIn: "Try signing in normally",
  requestNewInvitation: "request a new invitation",
  orSeparator: "or",
  resetPassword: {
    title: "Set a new password",
    description: "Choose a strong password for your account",
    newPasswordPlaceholder: "New password",
    confirmPasswordPlaceholder: "Confirm new password",
    passwordsDontMatch: "Passwords do not match",
    updateButton: "Update password",
  },
  errors: {
    unexpected: "An unexpected error occurred",
    invalidCredentials: "Invalid email or password. Please check your credentials and try again.",
    emailExists: "An account with this email already exists. Try signing in instead.",
    passwordTooShort: "Password must be at least 6 characters long.",
    invalidEmail: "Please enter a valid email address.",
    signupDisabled: "Account creation is currently disabled. Please contact support.",
    rateLimited: "Too many emails sent. Please wait a few minutes before trying again.",
    linkExpired: "This link has expired. Please request a new invitation or reset link.",
    somethingWentWrong: "Something went wrong",
    acceptTerms: "Please accept the Terms of Service and Privacy Policy to continue.",
    passwordRequirementsPrefix: "Password must meet all requirements:",
    inviteExpired: "Your invitation or reset link has expired. Please request a new one.",
    invitationExpired: "This invitation has expired. Please contact the organization admin for a new invitation.",
    accessDenied: "Access denied. The invitation link may be invalid or expired.",
    contactAdminResend: "Please contact the organization admin to resend your invitation.",
    resetAllRequirements: "Password must meet all requirements.",
    resetPasswordsDontMatch: "Passwords do not match.",
    resetFailed: "Failed to reset password",
    resetGeneric: "Something went wrong. Please try again.",
    verifyHuman: "We could not confirm that you are a person. Please try again.",
    passwordLeaked: "This password is on a list of leaked passwords. Choose a different password.",
    passwordSame: "This is your current password. Choose a new password.",
  },
  invitation: {
    loading: "Loading invitation...",
    errorTitle: "Invitation Error",
    notFound: "Invitation not found",
    noId: "No invitation ID provided",
    loadFailed: "Failed to load invitation",
    acceptFailed: "Failed to accept invitation",
    goToDashboard: "Go to Dashboard",
    alreadyAcceptedTitle: "Invitation Already Accepted",
    alreadyAcceptedDescription: "You have already joined {{org}}",
    expiredTitle: "Invitation Expired",
    revokedTitle: "Invitation Revoked",
    invalidDescription: "This invitation is no longer valid. Please contact the organization admin for a new invitation.",
    emailMismatchTitle: "Email Mismatch",
    emailMismatchDescription: "This invitation was sent to {{invited}}, but you are signed in as {{current}}.",
    emailMismatchHelp: "Please sign in with the correct email address or contact the organization admin.",
    signInDifferentAccount: "Sign in with different account",
    title: "Organization Invitation",
    description: "You have been invited to join {{org}}",
    organizationLabel: "Organization",
    roleLabel: "Role",
    invitedEmailLabel: "Invited Email",
    expiresLabel: "Expires",
    signedInAs: "Signed in as {{email}}",
    accepting: "Accepting...",
    acceptButton: "Accept Invitation",
    signInToAcceptHelp: "You need to sign in to accept this invitation.",
    signInToAccept: "Sign in to Accept",
    acceptAsHelp: "You will be signed in as {{email}} when you accept.",
    acceptTermsCustomer: "I accept Verbu's {{terms}} and {{privacy}} on behalf of my organization",
    acceptTermsPartner: "I accept the Verbu {{agreement}} on behalf of my organization",
    termsLinkText: "Terms of Use",
    privacyLinkText: "Privacy Policy",
    partnerAgreementLinkText: "Partner Agreement",
    termsUrl: "https://www.verbu.com/legal/terms",
    privacyUrl: "https://www.verbu.com/legal/privacy",
    partnerAgreementUrl: "https://www.verbu.com/legal/partner-agreement",
  },
};
